import { Box, CircularProgress, useTheme } from '@mui/material';
import { useGetChallengeById, useUpdateChallenge } from '_api';
import { Challenge, ChallengePayload } from '_api/types';
import { AdminRouteMap } from '_constants';
import { SEVERITY } from '_constants/derived';
import { DefaultNewChallenge } from '_constants/object-templates';
import { FormHeaderText, TextButton } from 'components/atoms';
import { AlertMessage } from 'components/molecules';
import { ChallengeForm } from 'components/organisms/forms';
import React, { useEffect, useMemo, useState } from 'react';
import { IoArrowBackOutline } from 'react-icons/io5';
import { useParams } from 'react-router-dom';
import { useNavigateTo } from 'utils';

export const EditChallenge = () => {
  const navigate = useNavigateTo();
  const theme = useTheme();
  const { challengeId: id } = useParams();
  const challengeId = useMemo(() => (id !== undefined ? Number(id) : 0), [id]);
  const { data: apiChallenge, isLoading } = useGetChallengeById(challengeId);
  const { mutateAsync: updateChallenge } = useUpdateChallenge();

  const convertToExercisePayload = (initChallenge: Challenge) => {
    const {
      fullImageUrl: _fullImageUrl,
      fullVideoUrl: _fullVideoUrl,
      ...chal
    } = initChallenge;
    return {
      ...chal,
      equipment: chal.equipment ? chal.equipment.map((equip) => equip.id) : [],
    } as ChallengePayload;
  };

  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [challenge, setChallenge] = useState<ChallengePayload | undefined>(
    apiChallenge ? convertToExercisePayload(apiChallenge) : undefined,
  );

  useEffect(() => {
    if (id && apiChallenge) setChallenge(convertToExercisePayload(apiChallenge));
  }, [apiChallenge, id]);

  const onBack = () => {
    setChallenge(DefaultNewChallenge);
    history.back();
  };

  const onSubmit = (updatedChallenge: ChallengePayload) => {
    updateChallenge(updatedChallenge)
      .then((data) => {
        if (data && data.id) {
          setShowSuccess(true);
          setTimeout(() => {
            navigate(AdminRouteMap.LIBRARY);
          }, 1500);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          setErrorMessage(
            'Unexpected error occured. Please refresh the page and try again.',
          );
        } else if (error.message) {
          setErrorMessage(`ERROR: ${error.message}`);
        } else {
          setErrorMessage('Unexpected error. Please Try again.');
        }
      });
  };

  return (
    <Box style={{ padding: 0 }}>
      <Box>
        <FormHeaderText variant="h4" gutterBottom sx={{ textAlign: 'left' }}>
          Edit Functional Challenge
        </FormHeaderText>
        <TextButton
          onClick={onBack}
          sx={{ color: theme.palette.primary.main, marginBottom: '10px', paddingLeft: 0 }}
        >
          <IoArrowBackOutline />
          BACK
        </TextButton>
      </Box>
      {!isLoading && challenge && challenge.id === challengeId ? (
        <ChallengeForm
          disableAll={showSuccess}
          initValue={challenge}
          onBack={onBack}
          onSubmit={onSubmit}
        />
      ) : (
        <CircularProgress />
      )}
      <AlertMessage
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={'Challenge successfully created'}
      />
      <AlertMessage
        open={!!errorMessage}
        onClose={() => setErrorMessage('')}
        severity={SEVERITY.error}
        message={errorMessage}
      />
    </Box>
  );
};
